import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import dayjs from "dayjs";
import ContractPreview from "../components/ContractPreview";
import { toast } from "react-hot-toast";
import html2pdf from "html2pdf.js";
import { useRef } from "react";
import CryptoJS from "crypto-js";

export default function ContractGenerator() {
  const [clients, setClients] = useState<any[]>([]);
  const contractRef = useRef(null);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [clientData, setClientData] = useState<any>(null);
  const [termMode, setTermMode] = useState<"without_trial" | "with_trial">("without_trial");
  const [form, setForm] = useState({
    contract_start_date: dayjs().format("YYYY-MM-DD"),
    setup_fee_sgd: 300,
    base_subscription_sgd: 150,
    additional_message_fee_sgd: 0.1,
    included_messages: 1000,
    trial_duration: null as number | null,
    uen: "",
    registered_address: "",
    contract_signed_date: dayjs().format("YYYY-MM-DD"),
    client_signatory_name: "",
    client_signatory_title: "",
    template_message_fee_sgd: 0.2,
    pdpa_notice_optout: false,
  });

  useEffect(() => {
    supabase.from("clients").select("id, raison_sociale").then(({ data }) => {
      setClients(data || []);
    });
  }, []);

  useEffect(() => {
    if (!selectedClientId) return;
    supabase.from("clients").select("*").eq("id", selectedClientId).single().then(({ data }) => {
      setClientData(data);
      setForm((prev) => ({
        ...prev,
        ...data,
        // Include the PDPA opt-out clause whenever the client's automatic
        // notices are disabled; the admin can still override via the checkbox.
        pdpa_notice_optout: data?.pdpa_consent_messages_enabled === false,
      }));
      setTermMode(data?.trial_duration === null || data?.trial_duration === undefined ? "without_trial" : "with_trial");
    });
  }, [selectedClientId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, value } = e.target;

    if (type === "number") {
      const parsedValue = value === "" ? null : Number(value);
      setForm({ ...form, [name]: parsedValue });
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const downloadSignedContract = () => {
    const contractUrl = clientData?.contract_url;
    if (!contractUrl) {
      toast.error("No signed contract is available.");
      return;
    }

    const filename = `${clientData.raison_sociale}_signed_contract.pdf`;
    const signedContractUrl = new URL(contractUrl);
    signedContractUrl.searchParams.set("download", filename);
    const link = document.createElement("a");
    link.href = signedContractUrl.toString();
    link.download = filename;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const generateAndSignContract = async () => {
    const signingClientId = selectedClientId;
    if (!contractRef.current || !clientData) {
      toast.error("Missing contract content or client data.");
      return;
    }
  
    toast.loading("Generating contract...");
  
    try {
      // 1. Générer PDF Blob depuis DOM
      const opt = {
        margin: 0.5,
        filename: `${clientData.raison_sociale}_contract.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      };
  
      const worker = html2pdf().set(opt).from(contractRef.current);
      const blob = await worker.outputPdf("blob");
  
      // 2. Calculer le hash SHA256
      const arrayBuffer = await blob.arrayBuffer();
      const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer as any);
      const hash = CryptoJS.SHA256(wordArray).toString();
  
      // 3. Upload PDF dans Supabase Storage
      const filePath = `contracts/${signingClientId}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(filePath, blob, { upsert: true, contentType: "application/pdf" });
  
      if (uploadError) throw uploadError;
  
      const { data: publicUrlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(filePath);
  
      const publicUrl = publicUrlData?.publicUrl;
  
      if (!publicUrl) throw new Error("Could not retrieve public URL");
  
      // 4. Mise à jour de la table clients
      const { error: updateError } = await supabase
        .from("clients")
        .update({
          contract_url: publicUrl,
          contract_hash: hash,
          contract_signed_date: new Date().toISOString(),
          ailah_signed: true,
          contract_status: "signed",
        })
        .eq("id", signingClientId);
  
      if (updateError) throw updateError;

      setClientData((currentClient) => {
        if (currentClient?.id !== signingClientId) return currentClient;

        return {
          ...currentClient,
          contract_url: publicUrl,
        };
      });

      toast.dismiss();
      toast.success("✅ Contract signed and saved!");
    } catch (err) {
      console.error(err);
      toast.dismiss();
      toast.error("❌ Failed to generate and save contract");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">📝 Contract Generator</h1>

      <label className="block text-sm font-medium mb-1">Select client</label>
      <select
        className="mb-4 w-full border px-3 py-2 rounded"
        value={selectedClientId}
        onChange={(e) => setSelectedClientId(e.target.value)}
      >
        <option value="">-- Select client --</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>{c.raison_sociale}</option>
        ))}
      </select>

      {clientData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Contract term</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={termMode}
                onChange={(e) => setTermMode(e.target.value as "without_trial" | "with_trial")}
              >
                <option value="without_trial">without trial period</option>
                <option value="with_trial">with trial period</option>
              </select>
            </div>

            {termMode === "with_trial" && (
              <div>
                <label className="block text-sm font-medium">Trial duration (months)</label>
                <input
                  type="number"
                  name="trial_duration"
                  value={form.trial_duration ?? ""}
                  onChange={handleChange}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium">Contract Start Date</label>
              <input
                type="date"
                name="contract_start_date"
                value={form.contract_start_date}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Setup Fee (SGD)</label>
              <input
                type="number"
                name="setup_fee_sgd"
                value={form.setup_fee_sgd}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Monthly Subscription (SGD)</label>
              <input
                type="number"
                name="base_subscription_sgd"
                value={form.base_subscription_sgd}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Additional Message Fee (SGD)</label>
              <input
                type="number"
                name="additional_message_fee_sgd"
                value={form.additional_message_fee_sgd}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Included Messages / month</label>
              <input
                type="number"
                name="included_messages"
                value={form.included_messages}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Template Message Fee (SGD)</label>
              <input
                type="number"
                name="template_message_fee_sgd"
                value={form.template_message_fee_sgd}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Client UEN</label>
              <input
                type="text"
                name="uen"
                value={form.uen || ""}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Client Address</label>
              <input
                type="text"
                name="registered_address"
                value={form.registered_address || ""}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Contract Signed Date</label>
              <input
                type="date"
                name="contract_signed_date"
                value={form.contract_signed_date || ""}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Client Signatory Name</label>
              <input
                type="text"
                name="client_signatory_name"
                value={form.client_signatory_name || ""}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Client Signatory Title</label>
              <input
                type="text"
                name="client_signatory_title"
                value={form.client_signatory_title || ""}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-start gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={!!form.pdpa_notice_optout}
                  onChange={(e) => setForm({ ...form, pdpa_notice_optout: e.target.checked })}
                  className="mt-0.5 h-4 w-4"
                />
                <span>
                  PDPA notice opt-out clause
                  <span className="block font-normal text-gray-500">
                    Adds a clause stating that automatic PDPA storage notices are disabled at the
                    Client&apos;s request and that the Client assumes the notification obligation.
                    Pre-checked when the client&apos;s notices are disabled.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-4 my-6">
            <button
              onClick={async () => {
                if (!selectedClientId) {
                  toast.error("No client selected.");
                  return;
                }
            
                const { error } = await supabase
                  .from("clients")
                  .update({
                    contract_start_date: form.contract_start_date,
                    setup_fee_sgd: form.setup_fee_sgd,
                    base_subscription_sgd: form.base_subscription_sgd,
                    additional_message_fee_sgd: form.additional_message_fee_sgd,
                    included_messages: form.included_messages,
                    trial_duration: form.trial_duration,
                    uen: form.uen,
                    registered_address: form.registered_address,
                    contract_signed_date: form.contract_signed_date,
                    client_signatory_name: form.client_signatory_name,
                    client_signatory_title: form.client_signatory_title,
                    template_message_fee_sgd: form.template_message_fee_sgd,
                  })
                  .eq("id", selectedClientId);
            
                if (error) {
                  console.error("Save failed:", error);
                  toast.error("❌ Error saving contract data");
                } else {
                  toast.success("✅ Contract data saved!");
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded shadow"
            >
              💾 Save contract data
            </button>

            <button
              onClick={() => {
                if (!contractRef.current) return;
            
                const opt = {
                  margin: 0.5,
                  filename: `${clientData.raison_sociale}_contract.pdf`,
                  image: { type: 'jpeg', quality: 0.98 },
                  html2canvas: { scale: 2 },
                  jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
                };
            
                html2pdf().set(opt).from(contractRef.current).save();
              }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm px-4 py-2 rounded shadow"
            >
              🧾 Generate PDF
            </button>
            <button
              onClick={generateAndSignContract}
              className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded shadow"
            >
              ✅ Generate & Sign PDF
            </button>
            <div>
              <button
                onClick={downloadSignedContract}
                disabled={!clientData.contract_url}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:text-gray-600 text-white text-sm px-4 py-2 rounded shadow"
              >
                Download signed PDF
              </button>
              {!clientData.contract_url && (
                <p className="mt-1 text-xs text-gray-500">
                  Sign the contract to enable the signed PDF download.
                </p>
              )}
            </div>
          </div>
          <div ref={contractRef}>
            <ContractPreview client={clientData} form={form} termMode={termMode} />
          </div>
        </>
      )}
    </div>
  );
}
